import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Database,
  Activity,
  RefreshCw,
} from 'lucide-react';
import { getOpsStatus, type OpsStatus } from '../api/client';

function HealthBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <Clock className="h-3 w-3" />
        No data
      </span>
    );
  }

  const styles = {
    healthy: 'bg-green-100 text-green-700',
    stale: 'bg-yellow-100 text-yellow-700',
    error: 'bg-red-100 text-red-700',
    running: 'bg-blue-100 text-blue-700',
    unknown: 'bg-gray-100 text-gray-600',
  };

  const icons = {
    healthy: CheckCircle,
    stale: AlertCircle,
    error: XCircle,
    running: RefreshCw,
    unknown: Clock,
  };

  const Icon = icons[status as keyof typeof icons] || Clock;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || styles.unknown}`}
    >
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {status}
    </span>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-gray-400">-</span>;

  const styles = {
    completed: 'text-green-600',
    failed: 'text-red-600',
    running: 'text-blue-600',
    skipped: 'text-gray-500',
    pending: 'text-yellow-600',
  };

  return (
    <span className={`font-medium ${styles[status as keyof typeof styles] || 'text-gray-600'}`}>
      {status}
    </span>
  );
}

function formatDuration(seconds: number | string | null): string {
  if (seconds === null || seconds === undefined) return '-';
  const secs = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
  if (isNaN(secs)) return '-';
  if (secs < 60) return `${secs.toFixed(1)}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${Math.floor(secs % 60)}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

function formatTime(timestamp: string | null): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

function OverallHealth({ health }: { health: OpsStatus['overall_health'] }) {
  const styles = {
    healthy: 'bg-green-50 border-green-200 text-green-800',
    degraded: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };

  const icons = {
    healthy: CheckCircle,
    degraded: AlertCircle,
    error: XCircle,
  };

  const Icon = icons[health];
  const messages = {
    healthy: 'All systems operational',
    degraded: 'Some data sources are stale',
    error: 'Some data sources have errors',
  };

  return (
    <div className={`rounded-lg border p-4 ${styles[health]}`}>
      <div className="flex items-center gap-3">
        <Icon className="h-8 w-8" />
        <div>
          <h2 className="text-lg font-semibold capitalize">{health}</h2>
          <p className="text-sm opacity-80">{messages[health]}</p>
        </div>
      </div>
    </div>
  );
}

function DataCoverageCard({ coverage }: { coverage: OpsStatus['data_coverage'] }) {
  const items = [
    { label: 'Institutions', value: coverage.institutions },
    { label: 'Risk Scores', value: coverage.risk_scores },
    { label: 'Enforcement Records', value: coverage.enforcement_records },
    { label: 'Privacy Policies', value: coverage.privacy_policies },
    { label: 'Relationships', value: coverage.corporate_relationships },
  ];

  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-4">
        <Database className="h-5 w-5 text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">Data Coverage</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {items.map((item) => (
          <div key={item.label} className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">
              {item.value.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Status() {
  const {
    data: status,
    isLoading,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['ops', 'status'],
    queryFn: getOpsStatus,
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="p-6 bg-red-50 rounded-lg">
        <h2 className="text-lg font-semibold text-red-800">Error loading status</h2>
        <p className="text-red-600">{error?.message || 'Unknown error'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Status</h1>
          <p className="mt-2 text-gray-600">
            Data pipeline and source monitoring
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Overall health */}
      <OverallHealth health={status.overall_health} />

      {/* Data coverage */}
      <DataCoverageCard coverage={status.data_coverage} />

      {/* Data sources table */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Data Sources</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Health
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Last Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Last Run
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Records
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {status.data_sources.map((source) => (
                <tr key={source.source_code} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{source.source_name}</p>
                      <p className="text-xs text-gray-500">{source.source_code}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {source.source_type}
                  </td>
                  <td className="px-4 py-3">
                    <HealthBadge status={source.health_status} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={source.last_status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatTime(source.last_run_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-right">
                    {source.last_records_processed?.toLocaleString() || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-right">
                    {formatDuration(source.duration_seconds)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent runs */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Recent Pipeline Runs</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Job
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Started
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Records
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Duration
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Errors
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {status.recent_runs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No pipeline runs recorded yet
                  </td>
                </tr>
              ) : (
                status.recent_runs.map((run, idx) => (
                  <tr key={`${run.job_name}-${run.started_at}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {run.job_name}
                    </td>
                    <td className="px-4 py-3">
                      <HealthBadge status={run.health_status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatTime(run.started_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">
                      {run.records_processed.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">
                      {formatDuration(run.duration_seconds)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {run.errors && run.errors.length > 0 ? (
                        <span className="text-red-600" title={run.errors.join('\n')}>
                          {run.errors.length} error(s)
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        Last updated: {new Date(dataUpdatedAt).toLocaleTimeString()}
        {' | '}
        Auto-refresh every 60 seconds
      </div>
    </div>
  );
}
